import { COORDS_BY_CODE } from 'isomorphic-qwerty';

export type KeyCoordinate = [number, number];

export interface KeyboardLayout {
  id: string;
  name: string;
  hasIntlBackslash: boolean;
  hasBackslash: boolean;
  hasNumpad: boolean;
  excludeKeys?: Set<string>;
  keyMap: Record<string, KeyCoordinate>;
}
const ROW_STAGGER: Record<number, number> = { 0: 0, 1: 1, 2: 2, 3: 3 };

function iqToDCompose(iqX: number, iqY: number): KeyCoordinate {
  const ex = iqX + (ROW_STAGGER[iqY] ?? 0);
  return [2 * ex - iqY - 12, -ex + 7];
}
const NUMPAD_X_OFFSET = 20;

function numpadToDCompose(iqX: number, iqY: number): KeyCoordinate {
  return [2 * iqX - iqY + NUMPAD_X_OFFSET, -iqX + 4];
}
const LAYER1_KEY_MAP: Record<string, KeyCoordinate> = {};
const NUMPAD_KEY_MAP: Record<string, KeyCoordinate> = {};

for (const [code, [iqX, iqY, iqZ]] of COORDS_BY_CODE) {
  if (iqZ === 1) {
    LAYER1_KEY_MAP[code] = iqToDCompose(iqX, iqY);
  } else if (iqZ === 3) {
    NUMPAD_KEY_MAP[code] = numpadToDCompose(iqX, iqY);
  }
}
export const MODIFIER_ROW_KEYS = new Set([
  'ControlLeft', 'ControlRight',
  'AltLeft', 'AltRight',
  'MetaLeft', 'MetaRight',
  'Space',
  'ShiftLeft', 'ShiftRight',
  'CapsLock',
  'Tab',
  'Backspace',
  'Delete',
  'Enter',
  'ContextMenu',
  'Fn',
  'Escape',
  'F1','F2','F3','F4','F5','F6','F7','F8','F9','F10','F11','F12',
  'Insert','Home','End','PageUp','PageDown',
  'ArrowLeft','ArrowRight','ArrowUp','ArrowDown',
  'NumLock',
  'NumpadDecimal', 'NumpadEnter', 'NumpadEqual',
]);

export const SPECIAL_KEYS = {
  SUSTAIN: 'AltLeft',
  SUSTAIN_RIGHT: 'AltRight',
  VIBRATO: 'Space',
};
function buildKeyMap(opts: {
  hasIntlBackslash: boolean;
  hasBackslash: boolean;
  hasNumpad: boolean;
  excludeKeys?: Set<string>;
}): Record<string, KeyCoordinate> {
  const map: Record<string, KeyCoordinate> = {};

  for (const [code, coord] of Object.entries(LAYER1_KEY_MAP)) {
    if (MODIFIER_ROW_KEYS.has(code)) continue;
    if (opts.excludeKeys?.has(code)) continue;
    if (code === 'IntlBackslash' && !opts.hasIntlBackslash) continue;
    if (code === 'Backslash' && !opts.hasBackslash) continue;
    map[code] = coord;
  }

  if (opts.hasNumpad) {
    for (const [code, coord] of Object.entries(NUMPAD_KEY_MAP)) {
      if (MODIFIER_ROW_KEYS.has(code)) continue;
      if (opts.excludeKeys?.has(code)) continue;
      map[code] = coord;
    }
  }

  return map;
}
const SIXTY_PCT_EXCLUDE = new Set([
  'F1','F2','F3','F4','F5','F6','F7','F8','F9','F10','F11','F12',
  'PrintScreen','ScrollLock','Pause',
  'Insert','Delete','Home','End','PageUp','PageDown',
  'ArrowLeft','ArrowRight','ArrowUp','ArrowDown',
]);

const SIXTY_FIVE_PCT_EXCLUDE = new Set([
  'F1','F2','F3','F4','F5','F6','F7','F8','F9','F10','F11','F12',
  'PrintScreen','ScrollLock','Pause',
]);

const SEVENTY_FIVE_PCT_EXCLUDE = new Set([
  'PrintScreen','ScrollLock','Pause',
]);

const LAYOUTS_RAW: {
  id: string;
  name: string;
  hasIntlBackslash: boolean;
  hasBackslash: boolean;
  hasNumpad: boolean;
  excludeKeys?: Set<string>;
}[] = [
  { id: 'ansi',       name: 'ANSI (US QWERTY)',        hasIntlBackslash: false, hasBackslash: true,  hasNumpad: false },
  { id: 'ansi-np',    name: 'ANSI + Numpad',            hasIntlBackslash: false, hasBackslash: true,  hasNumpad: true  },
  { id: 'iso',        name: 'ISO (UK/EU QWERTY)',       hasIntlBackslash: true,  hasBackslash: true,  hasNumpad: false },
  { id: 'iso-np',     name: 'ISO + Numpad',             hasIntlBackslash: true,  hasBackslash: true,  hasNumpad: true  },
  { id: '75pct',      name: '75% (no nav cluster)',     hasIntlBackslash: false, hasBackslash: true,  hasNumpad: false, excludeKeys: SEVENTY_FIVE_PCT_EXCLUDE },
  { id: '65pct',      name: '65% (no F-row)',           hasIntlBackslash: false, hasBackslash: true,  hasNumpad: false, excludeKeys: SIXTY_FIVE_PCT_EXCLUDE },
  { id: '60pct',      name: '60% (compact)',            hasIntlBackslash: false, hasBackslash: true,  hasNumpad: false, excludeKeys: SIXTY_PCT_EXCLUDE },
  { id: '60pct-iso',  name: '60% ISO',                 hasIntlBackslash: true,  hasBackslash: true,  hasNumpad: false, excludeKeys: SIXTY_PCT_EXCLUDE },
  { id: 'dvorak',     name: 'ANSI (Dvorak)',            hasIntlBackslash: false, hasBackslash: true,  hasNumpad: false },
  { id: 'dvorak-iso', name: 'ISO (Dvorak)',             hasIntlBackslash: true,  hasBackslash: true,  hasNumpad: false },
];

export const KEYBOARD_VARIANTS: KeyboardLayout[] = LAYOUTS_RAW.map(raw => ({
  ...raw,
  keyMap: buildKeyMap(raw),
}));

export const layouts: Record<string, KeyboardLayout> = Object.fromEntries(
  KEYBOARD_VARIANTS.map(l => [l.id, l])
);

export function getLayout(id: string): KeyboardLayout {
  return layouts[id] ?? KEYBOARD_VARIANTS[0];
}

export function isDvorakLayout(id: string): boolean {
  return id.startsWith('dvorak');
}
const FIFTHS_NATURALS = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];
export function getNoteNameFromCoord(x: number): string {
  const baseIndex = ((x + 3) % 7 + 7) % 7;
  const baseName = FIFTHS_NATURALS[baseIndex];
  const accidentals = Math.floor((x + 3) / 7);
  if (accidentals === 0) return baseName;
  if (accidentals > 0) return baseName + buildSharps(accidentals);
  return baseName + buildFlats(-accidentals);
}

function buildSharps(n: number): string {
  let s = '';
  for (let i = 0; i < n; i++) s += '\u266F';
  return s;
}

function buildFlats(n: number): string {
  let s = '';
  for (let i = 0; i < n; i++) s += '\u266D';
  return s;
}
const PITCH_CLASS_12TET = ['C', 'C\u266F', 'D', 'E\u266D', 'E', 'F', 'F\u266F', 'G', 'A\u266D', 'A', 'B\u266D', 'B'];

export function get12TETName(x: number): string {
  const pc = ((2 + x * 7) % 12 + 12) % 12;
  return PITCH_CLASS_12TET[pc];
}

export function getCentDeviation(x: number, fifth: number): number {
  return x * (fifth - 700);
}
export const D_REF_MIDI = 62;

export function coordToMidi(x: number, y: number, octaveOffset = 0): number {
  return D_REF_MIDI + x * 7 + y * 12 + octaveOffset * 12;
}

export function coordToFrequency(
  x: number,
  y: number,
  octaveOffset = 0,
  generator: [number, number] = [700, 1200],
  baseFreq = 293.66
): number {
  const cents = y * generator[1] + x * generator[0] + octaveOffset * 1200;
  return baseFreq * Math.pow(2, cents / 1200);
}
const SUPERSCRIPT_DIGITS = '\u2070\u00B9\u00B2\u00B3\u2074\u2075\u2076\u2077\u2078\u2079';
const SUBSCRIPT_DIGITS = '\u2080\u2081\u2082\u2083\u2084\u2085\u2086\u2087\u2088\u2089';

function toSuperscript(n: number): string {
  return String(n).split('').map(d => SUPERSCRIPT_DIGITS[parseInt(d, 10)]).join('');
}

function toSubscript(n: number): string {
  return String(n).split('').map(d => SUBSCRIPT_DIGITS[parseInt(d, 10)]).join('');
}

const NOTE_NAMES_12 = ['C','C#','D','Eb','E','F','F#','G','Ab','A','Bb','B'];

export function pitchClassName(midi: number): string {
  return NOTE_NAMES_12[((midi % 12) + 12) % 12];
}

export function relativeOctave(midi: number, refMidi: number): number {
  return Math.floor((midi - refMidi) / 12);
}

export function octavePrefix(octave: number): string {
  if (octave === 0) return '\u2070';
  if (octave > 0) return toSuperscript(octave);
  return toSubscript(-octave);
}

export function formatNoteWithOctavePrefix(midi: number): string {
  return octavePrefix(relativeOctave(midi, D_REF_MIDI)) + pitchClassName(midi);
}
export type LabelLayout = 'qwerty' | 'dvorak';

const DVORAK_MAP: Record<string, string> = {
  KeyQ: "'", KeyW: ',', KeyE: '.', KeyR: 'P', KeyT: 'Y',
  KeyY: 'F', KeyU: 'G', KeyI: 'C', KeyO: 'R', KeyP: 'L',
  KeyA: 'A', KeyS: 'O', KeyD: 'E', KeyF: 'U', KeyG: 'I',
  KeyH: 'D', KeyJ: 'H', KeyK: 'T', KeyL: 'N',
  KeyZ: ';', KeyX: 'Q', KeyC: 'J', KeyV: 'K',
  KeyB: 'X', KeyN: 'B', KeyM: 'M',
  Semicolon: 'S', Quote: '-', Comma: 'W', Period: 'V', Slash: 'Z',
  Minus: '[', Equal: ']', BracketLeft: '/', BracketRight: '=',
};

export function getAllMappedKeys(): string[] {
  return Object.keys(KEYBOARD_VARIANTS[0].keyMap);
}

export function isKeyMapped(code: string): boolean {
  return code in KEYBOARD_VARIANTS[0].keyMap;
}

export function codeToLabel(code: string, labelLayout: LabelLayout = 'qwerty'): string {
  if (labelLayout === 'dvorak' && code in DVORAK_MAP) return DVORAK_MAP[code];
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit[0-9]$/.test(code)) return code.slice(5);
  if (/^Numpad[0-9]$/.test(code)) return 'N' + code.slice(6);
  switch (code) {
    case 'Backquote':       return '`';
    case 'Minus':           return '-';
    case 'Equal':           return '=';
    case 'BracketLeft':     return '[';
    case 'BracketRight':    return ']';
    case 'Backslash':       return '\\';
    case 'IntlBackslash':   return '\\';
    case 'Semicolon':       return ';';
    case 'Quote':           return "'";
    case 'Comma':           return ',';
    case 'Period':          return '.';
    case 'Slash':           return '/';
    case 'NumpadAdd':       return 'N+';
    case 'NumpadSubtract':  return 'N-';
    case 'NumpadMultiply':  return 'N*';
    case 'NumpadDivide':    return 'N/';
    case 'NumpadDecimal':   return 'N.';
    default:                return '';
  }
}

export const standardLayout: KeyboardLayout = KEYBOARD_VARIANTS[0];
